import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import API from "../util/API";
import { profileInfo } from "../features/userAuthSlice";

const DISMISS_KEY = "cherrfy_email_verify_banner_dismissed";

const todayStamp = () => new Date().toISOString().slice(0, 10);

const readDismissedToday = () => {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(DISMISS_KEY) === todayStamp();
  } catch {
    return false;
  }
};

// Slim strip under the header for signed-in users whose email is still
// unverified. Dismiss is per-day (localStorage flag), never permanent.
const EmailVerifyBanner = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.userAuth?.user);
  const [dismissed, setDismissed] = useState(readDismissedToday);
  const [sending, setSending] = useState(false);

  const unverified = Boolean(user) && user?.emailVerified === false;
  if (!unverified || dismissed) return null;

  const email = String(user?.email || "").trim();

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, todayStamp());
    } catch {
      // ignore storage failures
    }
    setDismissed(true);
  };

  const handleSendLink = async () => {
    if (sending) return;
    setSending(true);
    try {
      await API.post("/user/resend-verification", {});
      toast.success(email ? `Sent to ${email}` : "Verification link sent.");
      // Hydrate the freshest profile so the banner disappears once the
      // server flips emailVerified after the user clicks the link.
      dispatch(profileInfo());
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn’t send the link. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="status"
      className="border-t border-amber-200 bg-amber-50 text-amber-900"
    >
      <div className="mx-auto flex w-full max-w-[1680px] items-center gap-2 px-4 py-1.5 sm:px-5 lg:px-7 xl:px-12">
        <p className="min-w-0 flex-1 truncate text-xs">
          Confirm your email to protect your account
        </p>
        <button
          type="button"
          onClick={handleSendLink}
          disabled={sending}
          className="shrink-0 rounded-md border border-amber-400/60 bg-white/70 px-2.5 py-1 text-xs font-semibold text-amber-900 transition-colors hover:bg-white disabled:cursor-wait disabled:opacity-60"
        >
          {sending ? "Sending…" : "Send link"}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss email confirmation reminder"
          className="shrink-0 rounded-md p-1 text-amber-700 transition-colors hover:bg-amber-100"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default EmailVerifyBanner;
